/**
 * Ed Hardy Swim — mega menu behaviour.
 *
 * Desktop: hover and keyboard focus open the panel, with a small close delay so
 * the pointer can travel from the trigger into the panel without it snapping
 * shut. Mobile: a slide-in drawer with accordions.
 */

const DESKTOP = '(min-width: 990px)';

class EhMega extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-eh-item]')];
    this.drawer = this.querySelector('[data-eh-drawer]');
    this.scrim = this.querySelector('[data-eh-scrim]');
    this.mq = window.matchMedia(DESKTOP);

    this.items.forEach((item) => {
      const panel = item.querySelector('[data-eh-panel]');
      const link = item.querySelector('.eh-mega__top-link');
      if (!panel) return;

      item.addEventListener('pointerenter', () => this.open(item));
      item.addEventListener('pointerleave', () => this.scheduleClose(item));
      link.addEventListener('focus', () => this.open(item));
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) this.close(item);
      });
      /* Touch: first tap opens, second follows the link. */
      link.addEventListener('click', (e) => {
        if (!this.mq.matches) return;
        if (!item.classList.contains('is-open') && window.matchMedia('(hover: none)').matches) {
          e.preventDefault();
          this.open(item);
        }
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.items.forEach((i) => this.close(i));
        this.closeDrawer();
      }
    });

    const burger = this.querySelector('[data-eh-burger]');
    if (burger) burger.addEventListener('click', () => this.openDrawer());
    const x = this.querySelector('[data-eh-drawer-close]');
    if (x) x.addEventListener('click', () => this.closeDrawer());
    if (this.scrim) this.scrim.addEventListener('click', () => this.closeDrawer());

    this.querySelectorAll('[data-eh-acc]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        btn.nextElementSibling.hidden = open;
      });
    });
  }

  /*
   * Panel visibility is driven with inline styles rather than a class hook.
   * Horizon serves section CSS through constructed stylesheets, where a
   * descendant `.is-open` rule did not reliably win the cascade. Inline styles
   * are unambiguous and cost nothing here.
   */
  paint(item, open) {
    const panel = item.querySelector('[data-eh-panel]');
    if (!panel) return;
    panel.style.opacity = open ? '1' : '0';
    panel.style.visibility = open ? 'visible' : 'hidden';
    panel.style.transform = open ? 'none' : 'translateY(-6px)';
    panel.style.pointerEvents = open ? 'auto' : 'none';
  }

  open(item) {
    clearTimeout(this._t);
    if (!this.mq.matches) return;
    this.items.forEach((i) => i !== item && this.close(i));
    item.classList.add('is-open');
    this.paint(item, true);
    const link = item.querySelector('.eh-mega__top-link');
    if (link.hasAttribute('aria-expanded')) link.setAttribute('aria-expanded', 'true');
  }

  scheduleClose(item) {
    clearTimeout(this._t);
    this._t = setTimeout(() => this.close(item), 140);
  }

  close(item) {
    item.classList.remove('is-open');
    this.paint(item, false);
    const link = item.querySelector('.eh-mega__top-link');
    if (link && link.hasAttribute('aria-expanded')) link.setAttribute('aria-expanded', 'false');
  }

  openDrawer() {
    this.drawer.hidden = false;
    this.scrim.hidden = false;
    /* next frame so the transform transition runs */
    requestAnimationFrame(() => this.drawer.setAttribute('data-open', ''));
    document.documentElement.style.overflow = 'hidden';
    this.querySelector('[data-eh-burger]').setAttribute('aria-expanded', 'true');
    this.querySelector('[data-eh-drawer-close]').focus();
  }

  closeDrawer() {
    if (!this.drawer || this.drawer.hidden) return;
    this.drawer.removeAttribute('data-open');
    this.querySelector('[data-eh-burger]').setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
    setTimeout(() => {
      this.drawer.hidden = true;
      this.scrim.hidden = true;
    }, 240);
  }
}

if (!customElements.get('eh-mega')) customElements.define('eh-mega', EhMega);
